// Runtime configuration loader.
//
// In production (nginx container), /config.js is generated at container startup
// by envsubst on config.js.template, using runtime environment variables. In
// local dev (Vite dev server), /config.js is served from apps/wehunt-web/public/
// — see public/config.example.js. Either way, /config.js sets window.__APP_CONFIG__
// before the React bundle runs (see index.html).
//
// The bundle therefore has zero environment-specific values compiled in, which
// is what makes per-environment image promotion possible.

export interface FirebaseConfig {
  apiKey: string
  authDomain: string
  projectId: string
  storageBucket: string
  messagingSenderId: string
  appId: string
}

export interface DatadogConfig {
  applicationId: string
  clientToken: string
  service: string
  env: string
  version: string
}

/**
 * Which login the app offers, and where sso lives.
 *
 * Every field is a string because envsubst renders config.js: an unset
 * variable arrives as `""`. The meaning of each value is decided by
 * `readAuthConfig()` in @wehunt/core-web, which is the only reader; anything
 * other than an explicit "sso" leaves the app on its own login forms.
 */
export interface AuthConfig {
  mode?: string
  ssoBaseUrl?: string
  ssoClientId?: string
}

/**
 * Runtime feature flags.
 *
 * These ride the same runtime-config channel as everything else here, so a flag
 * flips by changing an environment variable on the Cloud Run service — no
 * rebuild, and the same promoted image serves every environment. That is the
 * whole reason config is loaded at runtime rather than compiled in.
 *
 * Every flag is optional and every reader must treat absence as OFF. An
 * environment whose config predates a flag then behaves exactly as it did
 * before the flag existed, which is what makes adding one safe to deploy ahead
 * of turning it on.
 */
export interface FeatureFlags {
  /**
   * Lets a drive in the plan-hunt wizard be based on a sub-area rather than the
   * whole ground, which narrows the stand pins the drive editor offers to those
   * inside that sub-area.
   */
  driveSubAreaScope?: boolean
  /**
   * The ended-hunt report page: an ended hunt opens the report layout instead of
   * the planning tabs.
   *
   * Gated because the report is being built slice by slice and does not yet cover
   * everything the tabbed page offers — notably the downed-game tab's add, edit
   * and remove. Until it does, replacing the page unconditionally would take a
   * shipped feature away.
   */
  campfireHuntReport?: boolean
  /**
   * Chronicle's NPS prompt: the bottom-right feedback card, its eligibility
   * request, and the dwell timer that arms it.
   *
   * Gated because an in-app survey is the most interruptive thing on the page and
   * the only way to stop a bad rollout is to stop asking — a flag flip beats a
   * revert. Off means the component never mounts, so no eligibility request is
   * made and no timer runs. Web-only: mobile has its own kill switch
   * (`feat_nps_dialog`, in Unleash) and the two are independent by design, so one
   * platform can be paused without silencing the other.
   */
  chronicleNpsPrompt?: boolean
  /**
   * The team member-invite button on a team's Members tab, and the invite modal
   * it opens.
   *
   * Gated because the invite backend answers 503 until its environment has a
   * message transport configured (Notifications:Provider), and an entry point
   * that cannot do its job is the exact complaint this work set out to fix.
   * Staging turns it on where the transport is being brought up; production
   * stays off until that transport is live there, at which point the flag flips
   * with no rebuild.
   */
  teamInvite?: boolean
  /**
   * The "Invite guest" action on a published hunt's detail page, letting the
   * organiser invite someone outside the team by email or phone (CLUB-972).
   *
   * Gated for the same reason as {@link teamInvite}: the invite backend answers
   * 503 until its environment has a message transport, so the action must not
   * appear where it cannot deliver.
   */
  huntGuestInvite?: boolean
}

export interface LinksConfig {
  /** Base URL of the web shop for every market but Norway, e.g. https://wehunt.app. */
  shopUrl?: string
  /** Base URL of the Norwegian shop, e.g. https://wehunt.no. */
  shopUrlNo?: string
  /**
   * Comma-separated hosts of the link service that issue shared-link codes,
   * e.g. "share-wh.wehunt.app,share-whx.wehunt.app". Read through
   * `shareLinkDomains()`, never directly.
   */
  shareLinkDomains?: string
}

export interface AppConfig {
  firebase: FirebaseConfig
  // Optional — missing applicationId or clientToken means initDatadog no-ops.
  // The runtime loader does NOT validate datadog fields; absence is a feature
  // so local dev and E2E builds can run without a Datadog RUM application.
  datadog?: DatadogConfig
  // Optional, and not validated: an absent block means every flag is off.
  features?: FeatureFlags
  auth?: AuthConfig
  // Optional, and not validated: absent means Apple identity resolution is off
  // and Apple sign-in behaves exactly as it did before the resolver existed.
  // Base URL only (no path) — the resolver client appends its own path.
  identityResolverUrl?: string
  // Optional, and not validated: absent (and the literal "false") means the
  // Apple cross-team repair flow — consulting the server on every SUCCESSFUL
  // Apple sign-in, not just a failed one — never runs. A separate switch from
  // identityResolverUrl on purpose: that URL is already provisioned in
  // production for the (failure-only) collision path, so it alone would put a
  // live cross-origin POST — with the power to swap the session — on every
  // successful Apple sign-in the moment this ships. Read directly from
  // window.__APP_CONFIG__ by appleResolveOnSuccessEnabled() in libs/web/core's
  // appleRepairFlow.ts (this type is documentation, not enforcement — same
  // pattern as identityResolverUrl above). Arrives as a STRING in a deployed
  // environment (envsubst), so only the string "true" (or a real boolean,
  // case-insensitively) turns it on — same rule as isFeatureEnabled below.
  appleResolveOnSuccess?: string
  // Optional, and not validated: an absent block, an empty value or an
  // unsubstituted "${SHOP_URL}" placeholder all mean "no shop links", and the
  // sidebar simply omits them. Read live from window.__APP_CONFIG__ by
  // readShopLinks() in shopLinks.ts, the same way isFeatureEnabled reads flags.
  links?: LinksConfig
}

declare global {
  interface Window {
    __APP_CONFIG__?: AppConfig
  }
}

function readAppConfig(): AppConfig {
  const cfg = window.__APP_CONFIG__
  if (!cfg) {
    throw new Error(
      'App config not loaded. Expected /config.js to set window.__APP_CONFIG__ before the bundle ran. For local dev, create apps/wehunt-web/public/config.js from public/config.example.js.',
    )
  }
  const requiredFirebase: ReadonlyArray<keyof FirebaseConfig> = [
    'apiKey',
    'authDomain',
    'projectId',
    'storageBucket',
    'messagingSenderId',
    'appId',
  ]
  for (const key of requiredFirebase) {
    if (!cfg.firebase?.[key]) {
      throw new Error(`Missing required firebase config field: ${key}`)
    }
  }
  return cfg
}

export const appConfig: AppConfig = readAppConfig()

/**
 * Reads a feature flag, defaulting to OFF.
 *
 * Values arrive as STRINGS in a deployed environment: config.js is produced by
 * envsubst, so an unset variable becomes `""` and a disabled one becomes the
 * literal `"false"` — which is truthy in JavaScript. Reading the field directly
 * would therefore turn every flag on in production while looking correct in a
 * test that assigns a real boolean. Only an explicit true (boolean `true` or
 * the string `"true"`, case-insensitive) enables a flag; anything else,
 * including a missing block or an unsubstituted `"${...}"` placeholder, is off.
 */
export function isFeatureEnabled(name: keyof FeatureFlags): boolean {
  // Read live from window rather than the `appConfig` snapshot taken at module
  // import, matching VersionBadge. The snapshot is captured the moment this
  // module is first imported, which makes the value impossible to vary in a
  // test and would silently ignore any later replacement of the config object.
  const raw: unknown = window.__APP_CONFIG__?.features?.[name]
  if (typeof raw === 'boolean') return raw
  if (typeof raw === 'string') return raw.trim().toLowerCase() === 'true'
  return false
}

/**
 * The link-service hosts a shared link's code may belong to, in the order to
 * try them.
 *
 * The value is one comma-separated string because envsubst renders config.js:
 * an unset variable arrives as `""`, and one missing from the envsubst list
 * arrives as the literal "${SHARE_LINK_DOMAINS}". Both mean "no domains" — the
 * caller then has nothing to resolve against, which is a safe state, whereas
 * treating the placeholder as a host would send a request to a nonsense URL.
 */
export function shareLinkDomains(): string[] {
  // Live from window rather than the `appConfig` snapshot, matching
  // isFeatureEnabled, so a test can vary it.
  const raw: unknown = window.__APP_CONFIG__?.links?.shareLinkDomains
  if (typeof raw !== 'string') return []
  return raw
    .split(',')
    .map((domain) => domain.trim())
    .filter((domain) => domain !== '' && !domain.startsWith('${'))
}
